import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Modal, IconButton, Tooltip } from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetZoomIcon,
} from '@mui/icons-material';

interface Props {
  uri: string;
  alt?: string;
}

export const ImagePreview: React.FC<Props> = ({ uri, alt = 'Receipt image' }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const zoomContainerRef = useRef<HTMLDivElement | null>(null);
  const zoomFocalPointRef = useRef<{ x: number; y: number } | null>(null);
  const lastZoomRef = useRef(1);
  const pinchStartRef = useRef<{ distance: number; zoom: number } | null>(null);

  const handleOpen = () => setIsZoomed(true);
  const handleClose = () => {
    setIsZoomed(false);
    setZoomLevel(1);
    lastZoomRef.current = 1;
  };

  const clampZoom = (value: number) => Math.min(3, Math.max(1, value));
  const setFocalPointToCenter = () => {
    const container = zoomContainerRef.current;
    if (!container) return;
    zoomFocalPointRef.current = {
      x: container.clientWidth / 2,
      y: container.clientHeight / 2,
    };
  };
  const handleToggleZoom = () => {
    setFocalPointToCenter();
    setZoomLevel((current) => (current > 1 ? 1 : 2));
  };
  const handleZoomIn = () => {
    setFocalPointToCenter();
    setZoomLevel((current) => clampZoom(current + 0.2));
  };
  const handleZoomOut = () => {
    setFocalPointToCenter();
    setZoomLevel((current) => clampZoom(current - 0.2));
  };
  const handleResetZoom = () => {
    setFocalPointToCenter();
    setZoomLevel(1);
  };
  const handleWheelZoom: React.WheelEventHandler<HTMLDivElement> = (event) => {
    if (event.deltaY === 0) return;
    event.preventDefault();
    const direction = event.deltaY > 0 ? -0.2 : 0.2;
    const bounds = event.currentTarget.getBoundingClientRect();
    zoomFocalPointRef.current = {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    };
    setZoomLevel((current) => clampZoom(current + direction));
  };
  const handleTouchStart: React.TouchEventHandler<HTMLDivElement> = (event) => {
    if (event.touches.length !== 2) return;
    event.preventDefault();
    const first = event.touches.item(0);
    const second = event.touches.item(1);
    if (!first || !second) return;
    const dx = second.clientX - first.clientX;
    const dy = second.clientY - first.clientY;
    const distance = Math.hypot(dx, dy);
    pinchStartRef.current = { distance, zoom: zoomLevel };
    const bounds = event.currentTarget.getBoundingClientRect();
    zoomFocalPointRef.current = {
      x: (first.clientX + second.clientX) / 2 - bounds.left,
      y: (first.clientY + second.clientY) / 2 - bounds.top,
    };
  };
  const handleTouchMove: React.TouchEventHandler<HTMLDivElement> = (event) => {
    if (event.touches.length !== 2 || !pinchStartRef.current) return;
    event.preventDefault();
    const first = event.touches.item(0);
    const second = event.touches.item(1);
    if (!first || !second) return;
    const dx = second.clientX - first.clientX;
    const dy = second.clientY - first.clientY;
    const distance = Math.hypot(dx, dy);
    const scale = distance / pinchStartRef.current.distance;
    const nextZoom = clampZoom(pinchStartRef.current.zoom * scale);
    const bounds = event.currentTarget.getBoundingClientRect();
    zoomFocalPointRef.current = {
      x: (first.clientX + second.clientX) / 2 - bounds.left,
      y: (first.clientY + second.clientY) / 2 - bounds.top,
    };
    setZoomLevel(nextZoom);
  };
  const handleTouchEnd: React.TouchEventHandler<HTMLDivElement> = (event) => {
    if (event.touches.length < 2) {
      pinchStartRef.current = null;
    }
  };

  useLayoutEffect(() => {
    const container = zoomContainerRef.current;
    if (!container) return;
    const previousZoom = lastZoomRef.current;
    if (previousZoom === zoomLevel) return;
    const focalPoint = zoomFocalPointRef.current ?? {
      x: container.clientWidth / 2,
      y: container.clientHeight / 2,
    };
    const zoomRatio = zoomLevel / previousZoom;
    const nextScrollLeft = (container.scrollLeft + focalPoint.x) * zoomRatio - focalPoint.x;
    const nextScrollTop = (container.scrollTop + focalPoint.y) * zoomRatio - focalPoint.y;
    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollLeft = Math.min(Math.max(0, nextScrollLeft), maxScrollLeft);
    container.scrollTop = Math.min(Math.max(0, nextScrollTop), maxScrollTop);
    zoomFocalPointRef.current = null;
    lastZoomRef.current = zoomLevel;
  }, [zoomLevel]);

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '3/4',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'grey.100',
          cursor: 'zoom-in',
          '&:hover': {
            '& .zoom-overlay': {
              opacity: 1,
            },
          },
        }}
        onClick={handleOpen}
      >
        <img
          src={uri}
          alt={alt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
        <Box
          className="zoom-overlay"
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            opacity: 0,
            transition: 'opacity 0.2s',
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: 1,
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
        </Box>
      </Box>

      <Modal
        open={isZoomed}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '90vw',
            height: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            p: 2,
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Tooltip title="Close (ESC)">
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              display: 'flex',
              gap: 1,
              bgcolor: 'rgba(0, 0, 0, 0.55)',
              borderRadius: 2,
              p: 0.5,
            }}
          >
            <Tooltip title="Zoom out">
              <span>
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 1}
                  size="small"
                  sx={{ color: 'white' }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reset zoom">
              <IconButton onClick={handleResetZoom} size="small" sx={{ color: 'white' }}>
                <ResetZoomIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom in">
              <span>
                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 3}
                  size="small"
                  sx={{ color: 'white' }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box
            sx={{
              width: '100%',
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              cursor: zoomLevel > 1 ? 'zoom-out' : 'zoom-in',
              touchAction: 'none',
            }}
            onClick={handleToggleZoom}
            onWheel={handleWheelZoom}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            ref={zoomContainerRef}
          >
            <img
              src={uri}
              alt={alt}
              style={{
                width: `${zoomLevel * 100}%`,
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto',
              }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};
